import React, { useState } from 'react';
import { Button } from 'antd';
import type { ChoiceGroup, ChoicesCardData } from '@ctd/agent-contract';
import style from '../style.module.scss';

const SEPARATOR = ', ';

export function pickedFrom(
    groups: ChoiceGroup[],
    answer: string | undefined
): (string | undefined)[] {
    let rest = answer;
    return groups.map((group, index) => {
        const last = index === groups.length - 1;
        const option = [...group.options]
            .sort((a, b) => b.length - a.length)
            .find((candidate) =>
                last
                    ? rest === candidate
                    : rest?.startsWith(candidate + SEPARATOR)
            );
        rest =
            option === undefined
                ? undefined
                : rest?.slice(option.length + SEPARATOR.length);
        return option;
    });
}

export const ChoicesCard: React.FC<{
    card: ChoicesCardData;
    answeredWith?: string;
    onAnswer?: (text: string) => void;
}> = ({ card, answeredWith, onAnswer }) => {
    const [picks, setPicks] = useState<(string | undefined)[]>(() =>
        pickedFrom(card.groups, answeredWith)
    );
    const shown =
        answeredWith === undefined
            ? picks
            : pickedFrom(card.groups, answeredWith);
    const single = card.groups.length === 1;

    const choose = (row: number, option: string) => {
        if (single) {
            onAnswer?.(option);
            return;
        }
        setPicks((previous) =>
            previous.map((pick, index) => (index === row ? option : pick))
        );
    };

    return (
        <div className={style.choices}>
            {card.groups.map((group, row) => (
                <div key={row} className={style.choice_group}>
                    <span className={style.choice_label}>{group.label}</span>
                    <div className={style.choice_options}>
                        {group.options.map((option, index) => {
                            const picked = shown[row] === option;
                            return (
                                <Button
                                    key={index}
                                    size="small"
                                    type={picked ? 'primary' : 'default'}
                                    className={
                                        picked ? style.choice_picked : undefined
                                    }
                                    aria-pressed={
                                        single && answeredWith === undefined
                                            ? undefined
                                            : picked
                                    }
                                    disabled={!onAnswer}
                                    onClick={() => choose(row, option)}
                                >
                                    {option}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            ))}
            {!single && (
                <Button
                    size="small"
                    aria-label="Send choices"
                    disabled={
                        !onAnswer || shown.some((pick) => pick === undefined)
                    }
                    onClick={() => onAnswer?.(shown.join(SEPARATOR))}
                >
                    Send
                </Button>
            )}
        </div>
    );
};
